import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getFeatured } from "@/utils/video-utils";

export default function HeroBanner() {
  const slides = getFeatured();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) go(diff > 0 ? 1 : -1);
  };

  const current = slides[index];

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Slide backgrounds */}
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.banner || slide.poster}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} - 嫩草视频`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div key={current.id} className="max-w-2xl ng-animate-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-primary-500 text-background-50 text-xs md:text-sm px-3 py-1 rounded-md">
                热门推荐
              </span>
              <span className="flex items-center gap-1 text-accent-400 text-sm">
                <i className="ri-star-fill"></i>
                {current.rating.toFixed(1)}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-foreground-950 leading-tight">
              {current.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground-700">
              <span>{current.category}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
              <span>{current.genres.join(" / ")}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
              <span>{current.region}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
              <span>{current.year}</span>
            </div>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed line-clamp-2 max-w-xl hidden sm:block">
              {current.summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="h-11 px-6 flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-xl"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${encodeURIComponent(current.category)}`}
                className="h-11 px-6 flex items-center gap-2 rounded-md bg-background-200/80 text-foreground-950 text-sm font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line text-xl"></i>
                更多同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一个"
        className="hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-100/70 text-foreground-950 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一个"
        className="hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-100/70 text-foreground-950 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 md:bottom-8 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到 ${slide.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-foreground-700/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}